import { alpha, type Theme, type SxProps } from "@mui/material/styles";

export const chargeSelectionStyles = (
  theme: Theme,
): Record<string, SxProps<Theme>> => ({
  root: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.charges.backgrounds.light,
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "60px",
  },
  headerTitle: {
    textAlign: "center",
    fontSize: "1.5rem",
    fontWeight: 600,
  },
  mainContent: {
    display: "flex",
    flexGrow: 1,
    padding: theme.spacing(3),
    overflow: "hidden",
    gap: theme.spacing(3),
  },
  heatListSection: {
    width: "350px",
    display: "flex",
    flexDirection: "column",
  },
  chargesSection: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
  },
  actionSection: {
    width: "360px",
  },
  operatorCard: {
    padding: theme.spacing(3),
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  tableCard: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
    position: "relative",
  },
  actionCard: {
    padding: theme.spacing(4),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    borderRadius: "8px",
  },
  tableContainer: {
    maxHeight: "100%",
    flex: 1,
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[50],
    "& th": {
      fontWeight: "bold",
      padding: theme.spacing(2.5),
      fontSize: "1.3rem",
    },
  },
  tableRow: {
    cursor: "pointer",
    height: "120px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& td": {
      padding: theme.spacing(2.5),
      fontSize: "1.5rem",
      borderBottom: `2px solid ${theme.palette.charges.borders.medium}`,
      fontWeight: 600,
    },
  },
  selectedRow: {
    backgroundColor: theme.palette.action.selected,
    "& td:first-child": {
      borderLeft: `8px solid ${theme.palette.primary.main}`,
    },
  },
  navigationButtons: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: "12px",
    marginLeft: theme.spacing(2),
    boxShadow: theme.shadows[3],
  },
  navButton: {
    margin: theme.spacing(2),
    width: "100px",
    height: "200px",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderRadius: "8px",
    "&.Mui-disabled": {
      backgroundColor: theme.palette.charges.borders.medium,
      color: theme.palette.charges.status.inactive,
    },
    transition: "all 0.2s ease",
  },
  navButtonIcon: {
    fontSize: "4rem",
  },
  actionButton: {
    padding: theme.spacing(3),
    fontSize: "1.5rem",
    fontWeight: 600,
    minHeight: "160px",
    textTransform: "none",
    boxShadow: theme.shadows[2],
    borderRadius: "8px",
  },
  disabledButton: {
    opacity: 0.6,
  },
  eafButton: {
    backgroundColor: theme.palette.grey[700],
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.grey[800],
    },
    fontSize: "1.5rem",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: theme.shadows[2],
    padding: theme.spacing(3),
    minHeight: "160px",
    borderRadius: "8px",
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: "white",
    fontSize: "1.5rem",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: theme.shadows[2],
    padding: theme.spacing(3),
    minHeight: "160px",
    borderRadius: "8px",
  },
  newChargeButton: {
    backgroundColor: theme.palette.success.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
    fontSize: "1.5rem",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: theme.shadows[2],
    padding: theme.spacing(3),
    minHeight: "160px",
    borderRadius: "8px",
  },
  statusCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
  },
  statusIndicator: {
    transform: "scale(1.8)",
  },
  navigationText: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textAlign: "center",
    fontSize: "1.1rem",
  },
  newChargePanel: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "1000px",
    zIndex: 1300,
    padding: theme.spacing(4),
    borderRadius: "12px",
    overflow: "visible",
  },
  heatTableCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    borderRadius: "8px",
  },
  heatTableContainer: {
    maxHeight: "100%",
    flex: 1,
  },
  heatTableHeader: {
    backgroundColor: theme.palette.charges.backgrounds.light,
    "& th": {
      backgroundColor: theme.palette.charges.backgrounds.light,
      color: theme.palette.text.secondary,
      fontStyle: "italic",
      fontWeight: "bold",
      padding: theme.spacing(2),
      fontSize: "1.2rem",
    },
  },
  heatTableRow: {
    cursor: "pointer",
    height: "120px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& td": {
      padding: theme.spacing(2.5),
      fontSize: "1.5rem",
      borderBottom: `2px solid ${theme.palette.charges.borders.medium}`,
      fontWeight: 600,
    },
  },
  heatSelectedRow: {
    backgroundColor: alpha(theme.palette.charges.status.active, 0.4),
    "&:hover": {
      backgroundColor: alpha(theme.palette.charges.status.active, 0.48),
    },
    "& td:first-child": {
      borderLeft: `8px solid ${theme.palette.primary.main}`,
    },
  },
  chargesTableCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    borderRadius: "8px",
  },
  chargesTableContainer: {
    maxHeight: "100%",
    flex: 1,
  },
  requestedWeightFooter: {
    minHeight: "112px",
    px: theme.spacing(3),
    py: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    borderTop: `2px solid ${theme.palette.charges.borders.medium}`,
    backgroundColor: theme.palette.charges.backgrounds.light,
  },
  requestedWeightLabel: {
    fontSize: "1.2rem",
    fontStyle: "italic",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  requestedWeightInput: {
    width: "220px",
    "& .MuiInputBase-root": {
      minHeight: "64px",
      fontSize: "2rem",
      fontWeight: 700,
      cursor: "pointer",
    },
    "& input": {
      textAlign: "right",
      cursor: "pointer",
    },
    "& input:placeholder-shown": {
      textAlign: "center",
    },
  },
  scaledTargetWeight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    lineHeight: 1.1,
  },
  originalTargetWeight: {
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
    fontWeight: 500,
    textDecoration: "line-through",
  },
  newTargetWeight: {
    fontSize: "inherit",
    fontWeight: "inherit",
  },
  magnitudeLabel: {
    color: theme.palette.text.secondary,
    fontSize: "1.2rem",
    fontStyle: "italic",
    fontWeight: 600,
  },
  chargesTotalsRow: {
    height: "90px",
    backgroundColor: theme.palette.charges.backgrounds.light,
    "& td": {
      padding: theme.spacing(2),
      fontSize: "1.55rem",
      fontWeight: 700,
      borderTop: `4px solid ${theme.palette.charges.borders.medium}`,
      borderBottom: `2px solid ${theme.palette.charges.borders.medium}`,
    },
  },
  chargesTableHeader: {
    backgroundColor: theme.palette.charges.backgrounds.light,
    "& th": {
      backgroundColor: theme.palette.charges.backgrounds.light,
      color: theme.palette.text.secondary,
      fontStyle: "italic",
      fontWeight: "bold",
      padding: "7px 16px",
      fontSize: "1.2rem",
      lineHeight: "24px",
    },
  },
  chargesTableRow: {
    cursor: "pointer",
    height: "100px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& td": {
      boxSizing: "border-box",
      height: "100px",
      whiteSpace: "nowrap",
      padding: theme.spacing(1, 2),
      fontSize: "1.45rem",
      borderBottom: `2px solid ${theme.palette.charges.borders.medium}`,
      fontWeight: 600,
    },
  },
  chargesSelectedRow: {
    backgroundColor: alpha(theme.palette.charges.status.active, 0.4),
    "&:hover": {
      backgroundColor: alpha(theme.palette.charges.status.active, 0.48),
    },
    "& td:first-child": {
      borderLeft: `8px solid ${theme.palette.primary.main}`,
    },
  },
  heatNavigationButtons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: "12px",
    marginTop: theme.spacing(2),
    boxShadow: theme.shadows[3],
  },
  heatNavButton: {
    margin: theme.spacing(0, 2),
    width: "150px",
    height: "80px",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderRadius: "8px",
    "&.Mui-disabled": {
      backgroundColor: theme.palette.charges.borders.medium,
      color: theme.palette.charges.status.inactive,
    },
    transition: "all 0.2s ease",
  },
  heatNavButtonIcon: {
    fontSize: "3rem",
  },
  heatNavigationContainer: {
    marginTop: "12px",
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  heatNavPrevButton: {
    minHeight: "56px",
    flex: "1",
    fontSize: "2rem",
    fontWeight: "bold",
    borderRadius: "8px",
    boxShadow: theme.shadows[1],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  heatNavPageNumber: {
    minWidth: "60px",
    minHeight: "56px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "20px",
    backgroundColor: theme.palette.charges.backgrounds.light,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "8px",
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[1],
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    flex: "0 0 auto",
  },
  heatNavNextButton: {
    minHeight: "56px",
    flex: "1",
    fontSize: "2rem",
    fontWeight: "bold",
    borderRadius: "8px",
    boxShadow: theme.shadows[1],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});
